import jsPDF from 'jspdf'
import { ControlType } from 'utility/constant/template'

// Helper function to strip HTML tags
const stripHTML = (html) => {
    if (typeof html === 'undefined' || html === null) return ''
    if (typeof document === 'undefined') {
        // Fallback for server-side rendering
        return html.replace(/<[^>]*>/g, '')
    }
    const temp = document.createElement('div')
    temp.innerHTML = html
    return temp.textContent || temp.innerText || ''
}

/**
 * Generates a PDF from template data
 * @param {Object} templateData - The template data from API response
 * @param {string} fileName - The name for the PDF file
 */
export const generateTemplatePDF = (templateData, fileName = 'template') => {
    if (!templateData) {
        return null
    }

    try {
        // eslint-disable-next-line new-cap
        const doc = new jsPDF()
        const pageWidth = doc.internal.pageSize.getWidth()
        const pageHeight = doc.internal.pageSize.getHeight()
        const margin = 20
        let y = 20

        const checkPageBreak = (spaceNeeded = 20) => {
            if (y + spaceNeeded > 280) {
                doc.addPage()
                y = 20
            }
        }

        // Title
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(18)
        doc.text(templateData?.TemplateName || 'Template', pageWidth / 2, y, {
            align: 'center',
        })
        y += 10
        doc.setDrawColor(200, 200, 200)
        doc.line(margin, y, pageWidth - margin, y)
        y += 15

        // Sections
        if (Array.isArray(templateData?.Sections)) {
            templateData.Sections.forEach((section, sectionIdx) => {
                checkPageBreak(25)
                doc.setFillColor(240, 240, 240)
                doc.rect(margin - 2, y - 8, pageWidth - margin * 2 + 4, 12, 'F')
                doc.setFontSize(13)
                doc.setTextColor(0, 0, 0)
                doc.text(`Section ${sectionIdx + 1}: ${section.SectionName}`, margin, y)
                y += 15

                section.Questions.forEach((q, questionIdx) => {
                    checkPageBreak(15)
                    const questionText = stripHTML(q.Question || '')
                    const questionType = q.QuestionType
                    const questionResponseId = q.QuestionResponseId

                    let displayText = `${questionIdx + 1}. ${questionText}`
                    if (questionType === 1 && q.Score > 0) {
                        displayText += ` (Score: ${q.Score})`
                    }

                    doc.setFont('helvetica', 'bold')
                    doc.setFontSize(11)
                    doc.setTextColor(0, 0, 0)
                    const wrappedQuestionLines = doc.splitTextToSize(
                        displayText,
                        pageWidth - margin * 2,
                    )
                    checkPageBreak(Math.max(15, wrappedQuestionLines.length * 6 + 2))
                    doc.text(wrappedQuestionLines, margin, y)
                    y += wrappedQuestionLines.length * 6 + 2

                    const drawTextBox = (isTextArea) => {
                        checkPageBreak(isTextArea ? 80 : 30)
                        const height = isTextArea ? 60 : 30
                        const boxWidth = pageWidth - margin * 2
                        doc.setDrawColor(200, 200, 200)
                        doc.setFillColor(250, 250, 250)
                        doc.rect(margin, y, boxWidth, height, 'FD')
                        y += height + 12
                    }

                    const drawSignatureBox = () => {
                        checkPageBreak(50)
                        doc.setTextColor(100, 100, 100)
                        doc.setFontSize(10)
                        doc.text('Signature:', margin, y)
                        y += 5
                        const boxWidth = pageWidth - margin * 2
                        doc.setDrawColor(200, 200, 200)
                        doc.rect(margin, y, boxWidth, 40)
                        y += 50
                    }

                    const drawSlider = () => {
                        checkPageBreak(15)
                        doc.setFontSize(10)
                        doc.text('Slider Value:', margin + 4, y)
                        doc.setDrawColor(180, 180, 180)
                        doc.line(margin + 30, y + 2, pageWidth - margin, y + 2)
                        y += 12
                    }

                    const drawMultipleCheckbox = (groupId) => {
                        const boxSize = 5
                        const boxMargin = 3
                        const multipleResp =
                            (templateData.TemplateResponseType?.MultipleResponse || []).filter(
                                (resp) => resp.GroupId === groupId,
                            )
                        const userMultipleResp =
                            (templateData.TemplateResponseType?.UserMultipleResponse || []).filter(
                                (resp) => resp.GroupId === groupId,
                            )

                        const isUserResponse = userMultipleResp.length > 0
                        const responses = isUserResponse ? userMultipleResp : multipleResp

                        responses.forEach((resp) => {
                            checkPageBreak(10)
                            doc.setDrawColor(150, 150, 150)
                            doc.rect(margin + 4, y - boxSize, boxSize, boxSize)
                            doc.setFont('helvetica', 'normal')
                            doc.setFontSize(10)
                            doc.setTextColor(0, 0, 0)
                            let optionText = resp.DisplayText || ''
                            if (isUserResponse && resp.IsScore === true) {
                                optionText += ` (Score: ${resp.Score ?? 0})`
                            }
                            doc.text(optionText, margin + boxSize + boxMargin + 8, y)
                            y += 8
                        })
                        y += 6
                    }

                    const drawEmptyLine = () => {
                        checkPageBreak(10)
                        doc.setDrawColor(180, 180, 180)
                        doc.line(margin + 10, y + 2, margin + 100, y + 2)
                        y += 10
                    }

                    if (questionType === 1) {
                        const responseType =
                            templateData.TemplateResponseType?.SingleResponses?.find(
                                (resp) => resp.Id === questionResponseId,
                            )
                        if (responseType) {
                            switch (responseType.Id) {
                                case ControlType.Signature:
                                    drawSignatureBox()
                                    break
                                case ControlType.TextAnswer:
                                    drawTextBox(false)
                                    break
                                case ControlType.TextArea:
                                    drawTextBox(true)
                                    break
                                case ControlType.Slider:
                                    drawSlider()
                                    break
                                default:
                                    drawEmptyLine()
                            }
                        } else {
                            drawEmptyLine()
                        }
                    } else if (questionType === 2) {
                        drawMultipleCheckbox(questionResponseId)
                    } else {
                        drawEmptyLine()
                    }

                    if (questionIdx !== section.Questions.length - 1) {
                        doc.setDrawColor(240, 240, 240)
                        doc.line(margin, y, pageWidth - margin, y)
                        y += 12
                    }
                })
            })
        } else {
            doc.setFontSize(12)
            doc.setTextColor(200, 0, 0)
            doc.text('No sections available in this template.', margin, y)
        }

        // Footer page numbers
        const pageCount = doc.internal.getNumberOfPages()
        for (let i = 1; i <= pageCount; i += 1) {
            doc.setPage(i)
            doc.setFontSize(8)
            doc.setTextColor(150, 150, 150)
            doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 10, {
                align: 'right',
            })
        }

        // Save the PDF
        const sanitizedFileName = fileName.replace(/[^a-z0-9\- ]/gi, '_')
        doc.save(`${sanitizedFileName}.pdf`)
        return undefined
    } catch (error) {
        console.error('Error generating PDF:', error)
        throw error
    }
}

/**
 * Generates a PDF and returns it as a blob (for email attachment)
 * @param {Object} templateData - The template data from API response
 * @returns {Promise<Blob>} PDF blob
 */
export const generateTemplatePDFBlob = (templateData) => {
    if (!templateData) {
        return null
    }

    try {
        // eslint-disable-next-line new-cap
        const doc = new jsPDF()
        const pageWidth = doc.internal.pageSize.getWidth()
        const pageHeight = doc.internal.pageSize.getHeight()
        const margin = 20
        let y = 20

        const checkPageBreak = (spaceNeeded = 20) => {
            if (y + spaceNeeded > 280) {
                doc.addPage()
                y = 20
            }
        }

        // Title
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(18)
        doc.text(templateData?.TemplateName || 'Template', pageWidth / 2, y, {
            align: 'center',
        })
        y += 10
        doc.setDrawColor(200, 200, 200)
        doc.line(margin, y, pageWidth - margin, y)
        y += 15

        // Sections
        if (Array.isArray(templateData?.Sections)) {
            templateData.Sections.forEach((section, sectionIdx) => {
                checkPageBreak(25)
                doc.setFillColor(240, 240, 240)
                doc.rect(margin - 2, y - 8, pageWidth - margin * 2 + 4, 12, 'F')
                doc.setFontSize(13)
                doc.setTextColor(0, 0, 0)
                doc.text(`Section ${sectionIdx + 1}: ${section.SectionName}`, margin, y)
                y += 15

                section.Questions.forEach((q, questionIdx) => {
                    checkPageBreak(15)
                    const questionText = stripHTML(q.Question || '')
                    const questionType = q.QuestionType
                    const questionResponseId = q.QuestionResponseId

                    let displayText = `${questionIdx + 1}. ${questionText}`
                    if (questionType === 1 && q.Score > 0) {
                        displayText += ` (Score: ${q.Score})`
                    }

                    doc.setFont('helvetica', 'bold')
                    doc.setFontSize(11)
                    doc.setTextColor(0, 0, 0)
                    const wrappedQuestionLines = doc.splitTextToSize(
                        displayText,
                        pageWidth - margin * 2,
                    )
                    checkPageBreak(Math.max(15, wrappedQuestionLines.length * 6 + 2))
                    doc.text(wrappedQuestionLines, margin, y)
                    y += wrappedQuestionLines.length * 6 + 2

                    const drawTextBox = (isTextArea) => {
                        checkPageBreak(isTextArea ? 80 : 30)
                        const height = isTextArea ? 60 : 30
                        const boxWidth = pageWidth - margin * 2
                        doc.setDrawColor(200, 200, 200)
                        doc.setFillColor(250, 250, 250)
                        doc.rect(margin, y, boxWidth, height, 'FD')
                        y += height + 12
                    }

                    const drawSignatureBox = () => {
                        checkPageBreak(50)
                        doc.setTextColor(100, 100, 100)
                        doc.setFontSize(10)
                        doc.text('Signature:', margin, y)
                        y += 5
                        const boxWidth = pageWidth - margin * 2
                        doc.setDrawColor(200, 200, 200)
                        doc.rect(margin, y, boxWidth, 40)
                        y += 50
                    }

                    const drawSlider = () => {
                        checkPageBreak(15)
                        doc.setFontSize(10)
                        doc.text('Slider Value:', margin + 4, y)
                        doc.setDrawColor(180, 180, 180)
                        doc.line(margin + 30, y + 2, pageWidth - margin, y + 2)
                        y += 12
                    }

                    const drawMultipleCheckbox = (groupId) => {
                        const boxSize = 5
                        const boxMargin = 3
                        const multipleResp =
                            (templateData.TemplateResponseType?.MultipleResponse || []).filter(
                                (resp) => resp.GroupId === groupId,
                            )
                        const userMultipleResp =
                            (templateData.TemplateResponseType?.UserMultipleResponse || []).filter(
                                (resp) => resp.GroupId === groupId,
                            )

                        const isUserResponse = userMultipleResp.length > 0
                        const responses = isUserResponse ? userMultipleResp : multipleResp

                        responses.forEach((resp) => {
                            checkPageBreak(10)
                            doc.setDrawColor(150, 150, 150)
                            doc.rect(margin + 4, y - boxSize, boxSize, boxSize)
                            doc.setFont('helvetica', 'normal')
                            doc.setFontSize(10)
                            doc.setTextColor(0, 0, 0)
                            let optionText = resp.DisplayText || ''
                            if (isUserResponse && resp.IsScore === true) {
                                optionText += ` (Score: ${resp.Score ?? 0})`
                            }
                            doc.text(optionText, margin + boxSize + boxMargin + 8, y)
                            y += 8
                        })
                        y += 6
                    }

                    const drawEmptyLine = () => {
                        checkPageBreak(10)
                        doc.setDrawColor(180, 180, 180)
                        doc.line(margin + 10, y + 2, margin + 100, y + 2)
                        y += 10
                    }

                    if (questionType === 1) {
                        const responseType =
                            templateData.TemplateResponseType?.SingleResponses?.find(
                                (resp) => resp.Id === questionResponseId,
                            )
                        if (responseType) {
                            switch (responseType.Id) {
                                case ControlType.Signature:
                                    drawSignatureBox()
                                    break
                                case ControlType.TextAnswer:
                                    drawTextBox(false)
                                    break
                                case ControlType.TextArea:
                                    drawTextBox(true)
                                    break
                                case ControlType.Slider:
                                    drawSlider()
                                    break
                                default:
                                    drawEmptyLine()
                            }
                        } else {
                            drawEmptyLine()
                        }
                    } else if (questionType === 2) {
                        drawMultipleCheckbox(questionResponseId)
                    } else {
                        drawEmptyLine()
                    }

                    if (questionIdx !== section.Questions.length - 1) {
                        doc.setDrawColor(240, 240, 240)
                        doc.line(margin, y, pageWidth - margin, y)
                        y += 12
                    }
                })
            })
        } else {
            doc.setFontSize(12)
            doc.setTextColor(200, 0, 0)
            doc.text('No sections available in this template.', margin, y)
        }

        // Footer page numbers
        const pageCount = doc.internal.getNumberOfPages()
        for (let i = 1; i <= pageCount; i += 1) {
            doc.setPage(i)
            doc.setFontSize(8)
            doc.setTextColor(150, 150, 150)
            doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 10, {
                align: 'right',
            })
        }

        // Return PDF as blob instead of saving
        const pdfBlob = doc.output('blob')
        return pdfBlob
    } catch (error) {
        console.error('Error generating PDF:', error)
        throw error
    }
}

/**
 * Generates a PDF and returns it as Base64 string (for email attachment)
 * Uses the same PDF generation logic but returns Base64 instead of saving
 * @param {Object} templateData - The template data from API response
 * @returns {string} Base64 encoded PDF string (without data URI prefix)
 */
export const generateTemplatePDFBase64 = (templateData) => {
    if (!templateData) {
        return null
    }

    try {
        // Use the same PDF generation code but output as Base64
        // eslint-disable-next-line new-cap
        const doc = new jsPDF()
        const pageWidth = doc.internal.pageSize.getWidth()
        const pageHeight = doc.internal.pageSize.getHeight()
        const margin = 20
        let y = 20

        const checkPageBreak = (spaceNeeded = 20) => {
            if (y + spaceNeeded > 280) {
                doc.addPage()
                y = 20
            }
        }

        // Title
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(18)
        doc.text(templateData?.TemplateName || 'Template', pageWidth / 2, y, {
            align: 'center',
        })
        y += 10
        doc.setDrawColor(200, 200, 200)
        doc.line(margin, y, pageWidth - margin, y)
        y += 15

        // Sections (same logic as generateTemplatePDF)
        if (Array.isArray(templateData?.Sections)) {
            templateData.Sections.forEach((section, sectionIdx) => {
                checkPageBreak(25)
                doc.setFillColor(240, 240, 240)
                doc.rect(margin - 2, y - 8, pageWidth - margin * 2 + 4, 12, 'F')
                doc.setFontSize(13)
                doc.setTextColor(0, 0, 0)
                doc.text(`Section ${sectionIdx + 1}: ${section.SectionName}`, margin, y)
                y += 15

                section.Questions.forEach((q, questionIdx) => {
                    checkPageBreak(15)
                    const questionText = stripHTML(q.Question || '')
                    const questionType = q.QuestionType
                    const questionResponseId = q.QuestionResponseId

                    let displayText = `${questionIdx + 1}. ${questionText}`
                    if (questionType === 1 && q.Score > 0) {
                        displayText += ` (Score: ${q.Score})`
                    }

                    doc.setFont('helvetica', 'bold')
                    doc.setFontSize(11)
                    doc.setTextColor(0, 0, 0)
                    const wrappedQuestionLines = doc.splitTextToSize(
                        displayText,
                        pageWidth - margin * 2,
                    )
                    checkPageBreak(Math.max(15, wrappedQuestionLines.length * 6 + 2))
                    doc.text(wrappedQuestionLines, margin, y)
                    y += wrappedQuestionLines.length * 6 + 2

                    const drawTextBox = (isTextArea) => {
                        checkPageBreak(isTextArea ? 80 : 30)
                        const height = isTextArea ? 60 : 30
                        const boxWidth = pageWidth - margin * 2
                        doc.setDrawColor(200, 200, 200)
                        doc.setFillColor(250, 250, 250)
                        doc.rect(margin, y, boxWidth, height, 'FD')
                        y += height + 12
                    }

                    const drawSignatureBox = () => {
                        checkPageBreak(50)
                        doc.setTextColor(100, 100, 100)
                        doc.setFontSize(10)
                        doc.text('Signature:', margin, y)
                        y += 5
                        const boxWidth = pageWidth - margin * 2
                        doc.setDrawColor(200, 200, 200)
                        doc.rect(margin, y, boxWidth, 40)
                        y += 50
                    }

                    const drawSlider = () => {
                        checkPageBreak(15)
                        doc.setFontSize(10)
                        doc.text('Slider Value:', margin + 4, y)
                        doc.setDrawColor(180, 180, 180)
                        doc.line(margin + 30, y + 2, pageWidth - margin, y + 2)
                        y += 12
                    }

                    const drawMultipleCheckbox = (groupId) => {
                        const boxSize = 5
                        const boxMargin = 3
                        const multipleResp =
                            (templateData.TemplateResponseType?.MultipleResponse || []).filter(
                                (resp) => resp.GroupId === groupId,
                            )
                        const userMultipleResp =
                            (templateData.TemplateResponseType?.UserMultipleResponse || []).filter(
                                (resp) => resp.GroupId === groupId,
                            )

                        const isUserResponse = userMultipleResp.length > 0
                        const responses = isUserResponse ? userMultipleResp : multipleResp

                        responses.forEach((resp) => {
                            checkPageBreak(10)
                            doc.setDrawColor(150, 150, 150)
                            doc.rect(margin + 4, y - boxSize, boxSize, boxSize)
                            doc.setFont('helvetica', 'normal')
                            doc.setFontSize(10)
                            doc.setTextColor(0, 0, 0)
                            let optionText = resp.DisplayText || ''
                            if (isUserResponse && resp.IsScore === true) {
                                optionText += ` (Score: ${resp.Score ?? 0})`
                            }
                            doc.text(optionText, margin + boxSize + boxMargin + 8, y)
                            y += 8
                        })
                        y += 6
                    }

                    const drawEmptyLine = () => {
                        checkPageBreak(10)
                        doc.setDrawColor(180, 180, 180)
                        doc.line(margin + 10, y + 2, margin + 100, y + 2)
                        y += 10
                    }

                    if (questionType === 1) {
                        const responseType =
                            templateData.TemplateResponseType?.SingleResponses?.find(
                                (resp) => resp.Id === questionResponseId,
                            )
                        if (responseType) {
                            switch (responseType.Id) {
                                case ControlType.Signature:
                                    drawSignatureBox()
                                    break
                                case ControlType.TextAnswer:
                                    drawTextBox(false)
                                    break
                                case ControlType.TextArea:
                                    drawTextBox(true)
                                    break
                                case ControlType.Slider:
                                    drawSlider()
                                    break
                                default:
                                    drawEmptyLine()
                            }
                        } else {
                            drawEmptyLine()
                        }
                    } else if (questionType === 2) {
                        drawMultipleCheckbox(questionResponseId)
                    } else {
                        drawEmptyLine()
                    }

                    if (questionIdx !== section.Questions.length - 1) {
                        doc.setDrawColor(240, 240, 240)
                        doc.line(margin, y, pageWidth - margin, y)
                        y += 12
                    }
                })
            })
        } else {
            doc.setFontSize(12)
            doc.setTextColor(200, 0, 0)
            doc.text('No sections available in this template.', margin, y)
        }

        // Footer page numbers
        const pageCount = doc.internal.getNumberOfPages()
        for (let i = 1; i <= pageCount; i += 1) {
            doc.setPage(i)
            doc.setFontSize(8)
            doc.setTextColor(150, 150, 150)
            doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 10, {
                align: 'right',
            })
        }

        // Return PDF as Base64 string (remove data URI prefix)
        const base64String = doc.output('datauristring').split(',')[1]
        return base64String
    } catch (error) {
        console.error('Error generating PDF Base64:', error)
        throw error
    }
}
